import { useState, type FormEvent } from 'react';
import { useAuth } from '@/context/AuthContext';
import { ApiError } from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { Field, TextInput } from '@/components/ui/Field';
import styles from './Login.module.css';

export function Login() {
  const { login } = useAuth();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (pending) return;
    setError(null);
    setPending(true);
    try {
      await login(username.trim(), password);
      // AuthProvider flips `authenticated`; App swaps to the dashboard.
    } catch (err) {
      setError(
        err instanceof ApiError ? err.message : 'Could not reach the server. Please try again.',
      );
      setPassword('');
    } finally {
      setPending(false);
    }
  };

  return (
    <div className={styles.wrap}>
      <div className={styles.card}>
        <div className={styles.brand}>
          <span className={styles.logo} aria-hidden="true">
            🌿
          </span>
          <span>
            <span className={styles.brandName}>
              Kon<b>Store</b>
            </span>
            <span className={styles.tag}>Admin</span>
          </span>
        </div>

        <div>
          <h1 className={styles.title}>Sign in</h1>
          <p className={styles.sub}>Manage products, orders, and inventory.</p>
        </div>

        <form className={styles.form} onSubmit={onSubmit}>
          {error && (
            <div className={styles.error} role="alert">
              {error}
            </div>
          )}
          <Field label="Username" htmlFor="login-user" required>
            <TextInput
              id="login-user"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              autoComplete="username"
              autoFocus
              required
            />
          </Field>
          <Field label="Password" htmlFor="login-pass" required>
            <TextInput
              id="login-pass"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password"
              required
            />
          </Field>
          <Button type="submit" block disabled={pending || !username || !password}>
            {pending ? 'Signing in…' : 'Sign in'}
          </Button>
        </form>

        <p className={styles.foot}>Staff access only. Sign-ins are rate-limited.</p>
      </div>
    </div>
  );
}
